import {isMac} from '@wandb/weave/common/util/browser';
import {useCallback, useEffect, useRef} from 'react';

import {hotkeyPressed} from '../../../util/hotkeys';
import {changeSliderValue} from '../utils/stepUtils';
import {useMediaBrowserModalContext} from './MediaBrowserModalContext';

/**
 * Checks if the given element is a form field that should receive keyboard input.
 * When a form field is focused, we should not intercept keyboard shortcuts.
 * Note: We exclude 'button' because buttons don't receive text input, and many
 * interactive elements (like slider thumbs) are implemented as buttons.
 */
const isFormField = (node?: Element | null): boolean => {
  if (!node || node.nodeType !== Node.ELEMENT_NODE) {
    return false;
  }
  const tagName = node.tagName.toLowerCase();
  return ['input', 'textarea', 'select'].includes(tagName);
};

/**
 * Handles keyboard shortcuts that are not migrated to the inputSystem:
 * step slider (Meta/Ctrl + Arrow), index slider (Alt + Arrow), and plain
 * ArrowLeft/Right for run navigation.
 *
 * Zoom shortcuts are registered via the `mediaBrowserModal` inputSystem scope
 * in MediaBrowserFullscreenModal. The slider shortcuts intentionally remain on
 * this ad-hoc listener for now.
 */
export const useModalKeyboardHandling = () => {
  const {
    navigation,
    selectRun,
    updateStep,
    updateIndex,
    sliderData,
    sliderConfig,
  } = useMediaBrowserModalContext();

  const sliderDataRef = useRef(sliderData);
  const sliderConfigRef = useRef(sliderConfig);
  const navigationRef = useRef(navigation);
  const updateStepRef = useRef(updateStep);
  const updateIndexRef = useRef(updateIndex);
  const selectRunRef = useRef(selectRun);

  useEffect(() => {
    sliderDataRef.current = sliderData;
    sliderConfigRef.current = sliderConfig;
    navigationRef.current = navigation;
    updateStepRef.current = updateStep;
    updateIndexRef.current = updateIndex;
    selectRunRef.current = selectRun;
  });

  /**
   * Handles step slider keyboard navigation (Meta/Ctrl + Arrow keys)
   */
  const handleStepSliderKeys = useCallback((e: KeyboardEvent): boolean => {
    if (isFormField(document.activeElement)) {
      return false;
    }

    const {stepSlider} = sliderDataRef.current;

    if (!stepSlider || stepSlider.max <= 0) {
      return false;
    }

    if (hotkeyPressed(e, 'nextStep')) {
      e.preventDefault();
      e.stopPropagation();
      const newValue = changeSliderValue(
        stepSlider.current,
        sliderConfigRef.current.stepStrideLength,
        {
          min: stepSlider.min,
          max: stepSlider.max,
          availableValues: stepSlider.steps,
          snapToAvailableValues: true,
        }
      );
      updateStepRef.current(newValue);
      return true;
    }

    if (hotkeyPressed(e, 'prevStep')) {
      e.preventDefault();
      e.stopPropagation();
      const newValue = changeSliderValue(
        stepSlider.current,
        -sliderConfigRef.current.stepStrideLength,
        {
          min: stepSlider.min,
          max: stepSlider.max,
          availableValues: stepSlider.steps,
          snapToAvailableValues: true,
        }
      );
      updateStepRef.current(newValue);
      return true;
    }
    return false;
  }, []);

  /**
   * Handles index slider keyboard navigation (Alt + Arrow keys)
   */
  const handleIndexSliderKeys = useCallback((e: KeyboardEvent): boolean => {
    if (isFormField(document.activeElement)) {
      return false;
    }

    const {indexSlider} = sliderDataRef.current;

    if (!e.altKey || !indexSlider || indexSlider.max <= 0) {
      return false;
    }

    if (e.key === 'ArrowRight') {
      e.preventDefault();
      e.stopPropagation();
      const newValue = changeSliderValue(indexSlider.current, 1, {
        min: indexSlider.min,
        max: indexSlider.max,
        snapToAvailableValues: false,
      });
      updateIndexRef.current(newValue);
      return true;
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      e.stopPropagation();
      const newValue = changeSliderValue(indexSlider.current, -1, {
        min: indexSlider.min,
        max: indexSlider.max,
        snapToAvailableValues: false,
      });
      updateIndexRef.current(newValue);
      return true;
    }

    return false;
  }, []);

  /**
   * Handles run navigation keyboard shortcuts (plain Arrow keys)
   */
  const handleNavigationKeys = useCallback((e: KeyboardEvent): boolean => {
    if (isFormField(document.activeElement)) {
      return false;
    }

    const {currentRun, availableRuns} = navigationRef.current;

    if (
      e.altKey ||
      e.metaKey ||
      e.ctrlKey ||
      !currentRun ||
      availableRuns.length <= 1
    ) {
      return false;
    }

    const currentRunIndex = availableRuns.findIndex(
      run => run.name === currentRun.name
    );

    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      e.stopPropagation();
      if (currentRunIndex > 0) {
        const previousRun = availableRuns[currentRunIndex - 1];
        selectRunRef.current(previousRun);
      }
      return true;
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      e.stopPropagation();
      if (currentRunIndex < availableRuns.length - 1) {
        const nextRun = availableRuns[currentRunIndex + 1];
        selectRunRef.current(nextRun);
      }
      return true;
    }

    return false;
  }, []);

  const handleModalKeyDown = useCallback(
    (e: KeyboardEvent): boolean => {
      return (
        handleStepSliderKeys(e) ||
        handleIndexSliderKeys(e) ||
        handleNavigationKeys(e)
      );
    },
    [handleStepSliderKeys, handleIndexSliderKeys, handleNavigationKeys]
  );

  return {
    handleStepSliderKeys,
    handleIndexSliderKeys,
    handleNavigationKeys,
    handleModalKeyDown,
  };
};
